import { z } from 'zod';
/**
 * `/chat` namespace WebSocket 이벤트 페이로드 — Phase 7 v1.1 / Plan 07-04 Task 1 (CHAT-03).
 *
 * ## Namespace 역할 (ARCHITECTURE §3)
 *
 * io.of('/chat') — 스트리머 room (`live:{streamerId}`) 단위. 외부 수집 (chzzk/soop/
 * youtube — Plan 07-03) + LOOP 네이티브 입력 (Plan 07-02) 이 공통 `chat.message.
 * received.v1` Outbox 이벤트로 수렴 → Redis Stream `chat:live:{streamerId}` →
 * apps/realtime SseBridge → `/chat` namespace emit('chat:message').
 *
 * ## Client → Server 이벤트
 *
 *   - `chat:join`  {roomId, token}  → ack {ok, code?}
 *       roomId: `live:{uuid}` 포맷. token: Cockpit JWT (HS256 HS chat:join:{streamerId}).
 *   - `chat:leave` {roomId}          → ack {ok}
 *       명시적 room leave — disconnect 시 socket.io 가 자동 leave 하므로 optional.
 *
 * ## Server → Client 이벤트
 *
 *   - `chat:message` ChatMessageEvent — Redis Stream → SseBridge → room fanout.
 *
 * ## LOOP → 외부 역송출 금지 (T12) — 본 계약에서 재확인
 *
 * chat:message 는 **수신 표시용** payload. 외부 플랫폼(chzzk/soop/youtube) 으로
 * publish 하는 scope/action 은 본 계약에 존재하지 않는다. 향후 역송출 제안 시
 * 본 파일 + Phase 7 SUMMARY 들의 3중 문서화가 L3 보안 리뷰 근거.
 */
// ---------------------------------------------------------------------------
// Client → Server: chat:join
// ---------------------------------------------------------------------------
/**
 * `chat:join` payload — 특정 스트리머 room 에 join.
 *
 * - `roomId`: `live:{streamerId}` — uuid 기반 고정 포맷. chat.gateway 가 streamerId
 *   를 역추출하여 Cockpit token scope 와 일치 검증.
 * - `token`: Cockpit JWT (HS256, scope=`chat:join:{streamerId}`, exp=30s). 발급 경로:
 *   `GET /api/v1/chat/cockpit-token?streamerId=` (SessionMiddleware 하 로그인 사용자).
 */
export const chatJoinPayloadSchema = z.object({
    roomId: z
        .string()
        .regex(/^live:[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i, {
        message: 'roomId must be live:{streamerId} with uuid',
    }),
    token: z.string().min(10, 'cockpit JWT token required'),
});
/**
 * `chat:leave` payload — 명시적 room leave.
 *
 * socket.io 는 disconnect 시 자동 leave 하므로 선택적 — tab switch 등 명시적 이탈에만 사용.
 */
export const chatLeavePayloadSchema = z.object({
    roomId: z.string().min(1).max(100),
});
// ---------------------------------------------------------------------------
// Server → Client: chat:message
// ---------------------------------------------------------------------------
/**
 * Chat message broadcast payload — ChatService.ingest 가 Outbox `chat.message.received.v1`
 * 로 발행한 후 SseBridge 가 `/chat` namespace 로 emit 하는 shape.
 *
 * Plan 07-02 ChatService 가 조립하는 payload (contentPreview 160자 절단) 와 정확히
 * 동일 필드 구조. apps/realtime 이 본 schema 로 재검증 후 client 에 fanout.
 *
 * **shadowed 정책 (T-07-02-05)**:
 *   - `shadowed=true` 인 메시지도 본 스키마 안에 담긴다 (서버가 발행 여부 결정).
 *   - client (LiveChatOverlay) 는 `shadowed=true && authorUnifiedFanId !== viewerUserId`
 *     조합을 **방어적으로** 숨겨 타인 노출을 예방 (서버 listByRoom 에도 동일 규칙 적용 —
 *     double-check 용).
 *
 * **PII 최소화 (T-07-02-09)**:
 *   - `contentPreview` 는 160자 절단 (ChatService sanitize). 원본 content 는 DB 에만.
 *   - `metadata` 는 플랫폼별 뱃지 (YouTube authorDetails 등) — 1KB 한도 내.
 */
export const chatMessageEventSchema = z.object({
    messageId: z.string().uuid(),
    roomId: z.string(),
    externalPlatform: z.enum(['chzzk', 'soop', 'youtube', 'loop']),
    externalMessageId: z.string().nullable().optional(),
    authorUnifiedFanId: z.string().uuid().nullable().optional(),
    authorDisplayName: z.string(),
    contentPreview: z.string(),
    shadowed: z.boolean(),
    createdAt: z.string().datetime(),
    metadata: z.record(z.unknown()).optional(),
});
//# sourceMappingURL=chat.js.map